import {
  createElement,
  type AriaAttributes,
  type ComponentType,
  type ImgHTMLAttributes,
  type ReactNode,
} from "react";

export type TootImagePrimitiveProps = ImgHTMLAttributes<HTMLImageElement> & {
  alt: string;
  aspectRatio?: string;
  height?: number | `${number}`;
  imageClassName?: string;
  src: string;
  width?: number | `${number}`;
};

export type TootLinkPrimitiveProps = {
  children?: ReactNode;
  className?: string;
  href: string;
  target?: string;
  rel?: string;
  title?: string;
  "aria-label"?: string;
  "aria-current"?: AriaAttributes["aria-current"];
};

export type TootPrimitiveRegistry = {
  Image: ComponentType<TootImagePrimitiveProps>;
  Link: ComponentType<TootLinkPrimitiveProps>;
};

export type TootPrimitiveName = keyof TootPrimitiveRegistry;

const FallbackImage = ({
  alt,
  aspectRatio: _aspectRatio,
  imageClassName,
  ...props
}: TootImagePrimitiveProps) =>
  createElement("img", { ...props, alt, className: imageClassName ?? props.className });

const FallbackLink = ({ children, rel, target, ...props }: TootLinkPrimitiveProps) => {
  const safeRel = target === "_blank" && rel === undefined ? "noopener noreferrer" : rel;

  return createElement("a", { rel: safeRel, target, ...props }, children);
};

const registry: TootPrimitiveRegistry = {
  Image: FallbackImage,
  Link: FallbackLink,
};

export function registerTootPrimitive<TName extends TootPrimitiveName>(
  name: TName,
  component: TootPrimitiveRegistry[TName],
) {
  registry[name] = component;
}

export function registerTootPrimitives(components: Partial<TootPrimitiveRegistry>) {
  if (components.Image) {
    registerTootPrimitive("Image", components.Image);
  }

  if (components.Link) {
    registerTootPrimitive("Link", components.Link);
  }
}

export function resolveTootPrimitive<TName extends TootPrimitiveName>(
  name: TName,
): TootPrimitiveRegistry[TName] {
  return registry[name];
}

export const registerComponent = registerTootPrimitive;
export const getComponent = resolveTootPrimitive;
